import React, { useState, useEffect } from "react";
import { mockData } from "../mock";
import { ArrowRight } from "lucide-react";
import { Button } from "./ui/button";

const HeroSection = () => {
  // スライドショー用の状態管理
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // 3つの画像を配列で管理
  const images = [
    mockData.hero.heroImage,
    mockData.caseStudy.images[0],
    mockData.caseStudy.images[1]
  ];

  // 3秒ごとに画像を自動切り替え
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [images.length]);

  return (
    <section className="relative overflow-hidden bg-[radial-gradient(1200px_600px_at_80%_-20%,rgba(59,130,246,0.12),transparent),linear-gradient(180deg,#F6FAFF_0%,#F9FBFE_100%)]">
      {/* 背景の薄いグリッド */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.35] [mask-image:linear-gradient(#000,transparent_85%)]">
        <div className="absolute inset-0 bg-[length:40px_40px] bg-[linear-gradient(to_right,rgba(2,6,23,0.06)_1px,transparent_1px),linear-gradient(to_bottom,rgba(2,6,23,0.06)_1px,transparent_1px)]" />
      </div>

      {/* ===== 全デバイス共通：KV（画像 + テキストオーバーレイ） - 全画面幅 ===== */}
      <div className="relative min-h-[520px] sm:min-h-[600px] lg:min-h-[700px] overflow-hidden">
              {/* 背景グラデーション */}
              <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-slate-900/20 to-slate-900/60 z-10"></div>

              {/* メインのモック（大） - KV背景（スライドショー） */}
              <div className="absolute inset-0 w-full h-full">
                {images.map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`商品画像 ${index + 1}`}
                    className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-1000 ${
                      index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                ))}
              </div>

              {/* インジケーター（どの画像が表示されているか） */}
              <div className="absolute bottom-4 sm:bottom-6 lg:bottom-8 left-1/2 transform -translate-x-1/2 z-50 flex space-x-2">
                {images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      index === currentImageIndex 
                        ? 'bg-white w-8' 
                        : 'bg-white/50 hover:bg-white/75'
                    }`}
                    aria-label={`画像 ${index + 1} を表示`}
                  />
                ))}
              </div>

              {/* テキストオーバーレイ（画像の上部に配置） */}
              <div className="absolute top-8 sm:top-12 lg:top-16 left-4 sm:left-8 lg:left-0 right-4 sm:right-8 lg:right-0 z-40 lg:w-full">
                <div className="lg:max-w-7xl lg:mx-auto lg:px-16">
                <p className="text-white/90 font-bold text-xs sm:text-sm lg:text-base mb-2 sm:mb-3 tracking-wide uppercase drop-shadow-lg">
                  3D Mockup Service
                </p>

                <h1 className="font-heading tracking-tight text-white leading-[1.1] text-3xl sm:text-5xl lg:text-6xl drop-shadow-[0_4px_12px_rgba(0,0,0,0.5)]">
                  <span className="block font-bold">撮影不要</span>
                  <span className="block font-black bg-gradient-to-r from-blue-400 via-cyan-300 to-green-400 bg-clip-text text-transparent drop-shadow-none mt-1 sm:mt-2">
                    最短<span className="number-font-bold">1</span>日で
                  </span>
                  <span className="block font-bold mt-1 sm:mt-2">売れる商品画像を</span>
                </h1>

                <p className="mt-3 sm:mt-5 lg:mt-6 text-sm sm:text-base lg:text-lg text-white/95 font-medium leading-relaxed drop-shadow-lg max-w-md lg:max-w-2xl">
                  3Dモックアップで、やり取りや撮影工程を最小化<br />
                  CTR・ブランド力・SEO を同時に底上げ
                </p>

                {/* メトリクス - KV内に表示（グラスモーフィズムデザイン） */}
                <div className="grid grid-cols-3 gap-2 sm:gap-3 lg:gap-4 mt-5 sm:mt-7 lg:mt-8 max-w-3xl">
                  {/* 納期 */}
                  <div className="group relative backdrop-blur-sm bg-white/5 border border-white/15 rounded-2xl px-3 py-4 sm:px-4 sm:py-5 text-white shadow-[0_8px_32px_rgba(0,0,0,0.2)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.3)] hover:bg-white/10 hover:backdrop-blur-md transition-all duration-300 hover:-translate-y-1 text-center overflow-hidden">
                    <div className="relative z-10">
                      <div className="text-xs sm:text-sm font-bold text-white mb-1.5 sm:mb-2 tracking-wider uppercase drop-shadow-md">最短納期</div>
                      <div className="text-3xl sm:text-5xl font-black number-font-bold leading-none mb-0.5 sm:mb-1 drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
                        <span className="number-font-bold text-blue-400">1</span><span className="text-xl sm:text-3xl text-blue-400">日</span>
                      </div>
                      <div className="h-1 w-8 sm:w-12 bg-gradient-to-r from-blue-400 via-blue-500 to-cyan-400 mx-auto mt-1.5 sm:mt-2 rounded-full"></div>
                    </div>
                  </div>

                  {/* コスト */}
                  <div className="group relative backdrop-blur-sm bg-white/5 border border-white/15 rounded-2xl px-3 py-4 sm:px-4 sm:py-5 text-white shadow-[0_8px_32px_rgba(0,0,0,0.2)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.3)] hover:bg-white/10 hover:backdrop-blur-md transition-all duration-300 hover:-translate-y-1 text-center overflow-hidden">
                    <div className="relative z-10">
                      <div className="text-xs sm:text-sm font-bold text-white mb-1.5 sm:mb-2 tracking-wider uppercase drop-shadow-md">コスト削減</div>
                      <div className="text-3xl sm:text-5xl font-black number-font-bold leading-none mb-0.5 sm:mb-1 drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)]">
                        <span className="number-font-bold text-green-400">85</span><span className="text-xl sm:text-3xl text-green-400">%</span>
                      </div>
                      <div className="h-1 w-8 sm:w-12 bg-gradient-to-r from-green-400 via-green-500 to-emerald-400 mx-auto mt-1.5 sm:mt-2 rounded-full"></div>
                    </div>
                  </div>

                  {/* 品質 */}
                  <div className="group relative backdrop-blur-sm bg-white/5 border border-white/15 rounded-2xl px-3 py-4 sm:px-4 sm:py-5 text-white shadow-[0_8px_32px_rgba(0,0,0,0.2)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.3)] hover:bg-white/10 hover:backdrop-blur-md transition-all duration-300 hover:-translate-y-1 text-center overflow-hidden">
                    <div className="relative z-10">
                      <div className="text-xs sm:text-sm font-bold text-white mb-1.5 sm:mb-2 tracking-wider uppercase drop-shadow-md">高画質対応</div>
                      <div className="text-3xl sm:text-5xl font-black number-font-bold leading-none mb-0.5 sm:mb-1 drop-shadow-[0_2px_8px_rgba(0,0,0,0.8)] text-teal-400">
                        4K
                      </div>
                      <div className="h-1 w-8 sm:w-12 bg-gradient-to-r from-teal-400 via-teal-500 to-cyan-400 mx-auto mt-1.5 sm:mt-2 rounded-full"></div>
                    </div>
                  </div>
                </div>

                {/* ボタン - KV内（全デバイス） */}
                <div className="mt-4 sm:mt-5 lg:mt-6 mb-12 sm:mb-16 lg:mb-20 flex flex-col sm:flex-row gap-2 sm:gap-3 lg:gap-4 max-w-2xl">
                  <button
                    onClick={() =>
                      document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })
                    }
                    className="w-full sm:w-auto inline-flex items-center justify-center rounded-full bg-white/95 hover:bg-white backdrop-blur-sm text-gray-900 px-5 sm:px-8 h-10 sm:h-12 text-xs sm:text-sm font-bold transition-all shadow-[0_4px_16px_rgba(0,0,0,0.25)] hover:shadow-[0_6px_20px_rgba(0,0,0,0.35)] hover:-translate-y-0.5"
                  >
                    <span className="hidden sm:inline">無料で見積もりを依頼する</span>
                    <span className="sm:hidden">無料で見積もりを依頼</span>
                    <ArrowRight className="ml-1.5 h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </button>

                  <button
                    onClick={() =>
                      document.getElementById("case-study")?.scrollIntoView({ behavior: "smooth" })
                    }
                    className="w-full sm:w-auto inline-flex items-center justify-center rounded-full backdrop-blur-sm bg-white/10 hover:bg-white/20 border border-white/30 text-white px-5 sm:px-8 h-10 sm:h-12 text-xs sm:text-sm font-bold transition-all shadow-[0_4px_16px_rgba(0,0,0,0.25)] hover:shadow-[0_6px_20px_rgba(0,0,0,0.35)] hover:-translate-y-0.5"
                  >
                    制作事例を見る
                  </button>
                </div>
                </div>
              </div>
      </div>
    </section>
  );
};

export default HeroSection;